import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, Stack, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import AppSearch from '../../components/base/AppSearch';
import AidantTable from './components/AidantTable';
import AddAidantDialog from './components/AddAidantDialog';
import EditAidantDialog from './components/EditAidantDialog';
import ViewAidantDialog from './components/ViewAidantDialog';

const ITEMS_PER_PAGE = 8;

const Aidants = () => {
  const { user } = useAuth();
  const [allAidants, setAllAidants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [editingAidantId, setEditingAidantId] = useState(null);
  const [viewingAidantId, setViewingAidantId] = useState(null);

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem('token');

  const fetchAidants = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/admin/aidants`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const rawData = Array.isArray(response.data)
        ? response.data
        : response.data?.data || response.data?.aidants || [];

      setAllAidants(rawData.filter(Boolean));
    } catch (err) {
      console.error('Erreur attrapée :', err);
      toast.error('Erreur de chargement des aidants');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, token]);

  useEffect(() => {
    if (user) fetchAidants();
  }, [user, fetchAidants]);

 const filteredAidants = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return allAidants;

    return allAidants.filter((aidant) => {
      // 1. Recherche sur l'aidant lui-même (Nom / Prénom)
      const nomAidant = (aidant.nom || aidant.nom_aidant || '').toLowerCase();
      const prenomAidant = (aidant.prenom || aidant.prenom_aidant || '').toLowerCase();
      const matchAidant = `${nomAidant} ${prenomAidant}`.includes(term);

      // 2. Recherche sur les patients rattachés (Nom, Prénom, ID ou Téléphone)
      const matchPatient = aidant.patients && aidant.patients.some((p) => {
        const nomPatient = (p.nom_patient || '').toLowerCase();
        const prenomPatient = (p.prenom_patient || '').toLowerCase();
        const idPatient = String(p.id_patient_profile || '').toLowerCase();
        const telPatient = (p.telephone_patient || '').toLowerCase();

        return (
          `${nomPatient} ${prenomPatient}`.includes(term) ||
          idPatient.includes(term) ||
          telPatient.includes(term)
        );
      });

      return matchAidant || matchPatient;
    });
  }, [allAidants, searchTerm]);

  const totalItems = filteredAidants.length;
  const lastPage = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));

  const safePage = currentPage > lastPage ? 1 : currentPage;
  useEffect(() => {
    if (safePage !== currentPage) setCurrentPage(safePage);
  }, [safePage, currentPage]);

  const displayedAidants = useMemo(() => {
    const begin = (safePage - 1) * ITEMS_PER_PAGE;
    return filteredAidants.slice(begin, begin + ITEMS_PER_PAGE);
  }, [filteredAidants, safePage]);

  const handleView = (aidant) => {
    setViewingAidantId(aidant.id || aidant.id_aidant);
  };

  const handleEdit = (aidant) => {
    setEditingAidantId(aidant.id || aidant.id_aidant);
  };

  const handleDelete = async (aidant) => {
    const aidantId = aidant.id_aidant;
    const nomAidant = aidant.nom_aidant || 'cet aidant';

    if (
      window.confirm(
        `Voulez-vous vraiment supprimer l'aidant ${nomAidant} ainsi que ses affectations ?`
      )
    ) {
      try {
        await axios.delete(`${apiUrl}/admin/aidants/${aidantId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Aidant et ses affectations supprimés avec succès');
        fetchAidants();
      } catch (err) {
        console.error('Erreur suppression aidant :', err);
        toast.error('Erreur lors de la suppression de l\'aidant');
      }
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 4, flexWrap: 'wrap', gap: 2 }}
      >
        <Typography variant="h4" sx={{ fontWeight: 800, color: '#2B3674' }}>
          Gestion des Aidants
        </Typography>

        <Stack
  direction="row"
  spacing={2}
  alignItems="center"
  justifyContent="flex-end"
  sx={{ flexWrap: 'nowrap', width: '100%' }}
>
  <Box sx={{ width: '300px', minWidth: 0 }}>
    <AppSearch
      value={searchTerm}
      onChange={(val) => {
        setSearchTerm(val);
        setCurrentPage(1);
      }}
      placeholder="Rechercher un aidant..."
    />
  </Box>
  <Button
    variant="contained"
    startIcon={<AddIcon />}
    onClick={() => setOpenAddModal(true)}
    sx={{
      backgroundColor: '#4318FF',
      fontWeight: 700,
      borderRadius: '12px',
      textTransform: 'none',
      px: 3,
      py: 1,
      flexShrink: 0,
      '&:hover': { backgroundColor: '#3311CC' },
    }}
  >
    Ajouter un aidant
  </Button>
</Stack>
      </Stack>

      <AidantTable
        loading={loading}
        currentData={displayedAidants}
        currentPage={safePage}
        count={lastPage}
        totalItems={totalItems}
        itemsPerPage={ITEMS_PER_PAGE}
        apiUrl={apiUrl}
        token={token} // 👈 Ajouté ici pour permettre l'appel API d'affectation dans le tableau
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onPageChange={(newPage) => setCurrentPage(newPage)}
        onPatientAssigned={fetchAidants}
      />

      <AddAidantDialog
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        onAidantAdded={fetchAidants}
        apiUrl={apiUrl}
        token={token}
      />

      <EditAidantDialog
        open={!!editingAidantId}
        aidantId={editingAidantId}
        onClose={() => setEditingAidantId(null)}
        onAidantUpdated={fetchAidants}
        apiUrl={apiUrl}
        token={token}
      />

    <ViewAidantDialog
  open={!!viewingAidantId}
  aidantId={viewingAidantId}
  onClose={() => setViewingAidantId(null)}
  apiUrl={apiUrl}
  token={token}
  onPatientDetached={fetchAidants} // 👈 Permet de rafraîchir la liste générale des aidants
/>
    </Box>
  );
};

export default Aidants;